import { createFileRoute, Outlet, useLocation } from "@tanstack/react-router";
import { useEffect, useState } from "react";

import { SidebarInset, SidebarProvider } from "@diverge/ui/ui/sidebar";

import ViewAsBanner from "../components/ViewAsBanner";
import ExperimentModeBanner from "../components/ExperimentModeBanner";
import { useAuth } from "../context/AuthContext";
import { ExperimentModeProvider } from "../context/ExperimentModeContext";
import { SolutionExperimentProvider } from "../pages/Experiments/SolutionExperimentProvider";
import { useScope } from "../context/ScopeContext";
import { UnreadCommentsCountProvider } from "../context/UnreadCommentsCountContext";
import { AppShellProvider } from "../layouts/AppShellContext";
import AppSidebar from "../layouts/AppSidebar";
import Topbar from "../layouts/Topbar";
import { readStoredStringArray } from "../utils/api";

export const Route = createFileRoute("/_shell")({
  component: ShellLayout,
});

function readChatbotIdsFromStorage(): string[] {
  return readStoredStringArray("chatbot_ids");
}

function hasAnyChatbotAccess(chatbotIds: string[]): boolean {
  return Array.isArray(chatbotIds) && chatbotIds.length > 0;
}

/** Mid-size viewports (1024–1279px) start icon-collapsed; wide screens start expanded. */
function defaultSidebarOpen(): boolean {
  if (typeof window === "undefined" || typeof window.matchMedia !== "function") {
    return true;
  }
  return window.matchMedia("(min-width: 1280px)").matches;
}

function ShellLayout() {
  const location = useLocation();
  const auth = useAuth();
  const isAuthenticated = auth.isAuthenticated === true;
  const hasOrganizationContext = auth.hasOrganizationContext === true;
  const userSettings = auth.userSettings ?? null;

  const [chatbotIds, setChatbotIds] = useState<string[]>(() => readChatbotIdsFromStorage());
  const [selectedChatbotId, setSelectedChatbotId] = useState<string>(
    () => localStorage.getItem("selected_chatbot_id") || "ALL",
  );
  const [isSidebarOpen, setIsSidebarOpen] = useState<boolean>(() => defaultSidebarOpen());
  const [hasChatbotAccess, setHasChatbotAccess] = useState(() =>
    hasAnyChatbotAccess(readChatbotIdsFromStorage()),
  );

  const { scope: activeScope, chatbotIds: scopedChatbotIds } = useScope();

  useEffect(() => {
    const derived =
      activeScope.type === "bot"
        ? activeScope.id
        : scopedChatbotIds.length === 1
          ? scopedChatbotIds[0]!
          : "ALL";
    setSelectedChatbotId(derived);
    if (Array.isArray(scopedChatbotIds) && scopedChatbotIds.length > 0) {
      setChatbotIds(scopedChatbotIds);
      setHasChatbotAccess(true);
    }
  }, [activeScope, scopedChatbotIds]);

  useEffect(() => {
    if (!isAuthenticated) {
      setChatbotIds([]);
      setHasChatbotAccess(false);
      return;
    }

    const nextChatbotIds = readChatbotIdsFromStorage();
    setChatbotIds(nextChatbotIds);
    setHasChatbotAccess(hasAnyChatbotAccess(nextChatbotIds));
    setSelectedChatbotId(localStorage.getItem("selected_chatbot_id") || "ALL");
  }, [isAuthenticated, userSettings]);

  const isAuthScreen = location.pathname === "/login" || location.pathname === "/auth/callback";

  return (
    <ExperimentModeProvider>
      <AppShellProvider
        value={{
          chatbotIds,
          hasChatbotAccess,
          selectedChatbotId,
          setSelectedChatbotId,
          isSidebarOpen,
          setIsSidebarOpen,
        }}
      >
        <SolutionExperimentProvider>
          <UnreadCommentsCountProvider
            enabled={isAuthenticated && hasOrganizationContext}
            selectedChatbotId={selectedChatbotId}
          >
            {isAuthScreen ? (
              <div className="min-h-svh w-full bg-background">
                <Outlet />
              </div>
            ) : (
              <SidebarProvider open={isSidebarOpen} onOpenChange={setIsSidebarOpen}>
                <AppSidebar />
                {/* Integrated layout: sidebar shares the app surface and separates
                  with its hairline border; content fills the viewport. */}
                <SidebarInset className="h-svh overflow-hidden">
                  <Topbar />
                  <ViewAsBanner />
                  <ExperimentModeBanner />
                  <main className="min-h-0 flex-1 overflow-auto">
                    <Outlet />
                  </main>
                </SidebarInset>
              </SidebarProvider>
            )}
          </UnreadCommentsCountProvider>
        </SolutionExperimentProvider>
      </AppShellProvider>
    </ExperimentModeProvider>
  );
}
